import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Container,
  Avatar,
  Divider,
  Paper,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from '@mui/material';
import {
  ArrowBack,
  Commit,
  FileCopy,
  Settings,
  Visibility,
  ContentCopy,
  Refresh,
  ExpandMore,
  Code,
  Description,
  Delete,
} from '@mui/icons-material';
import { Project } from '../types';
import { gitLogAPI } from '../services/api';

// --- (Interfaces and helper functions remain the same) ---
interface SystemParametersGitHistoryProps {
  project: Project;
  onBack: () => void;
}

const SystemParametersGitHistory: React.FC<SystemParametersGitHistoryProps> = ({
  project,
  onBack,
}) => {
  const [loading, setLoading] = useState(true);
  const [systemParametersData, setSystemParametersData] = useState<any>(null);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [showFileDialog, setShowFileDialog] = useState(false);
  const [fileDialogTab, setFileDialogTab] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedFiles, setExpandedFiles] = useState<Set<string>>(new Set());

  // Function to mask sensitive values - permanent masking
  const maskSensitiveValue = (key: string, value: string): string => {
    const sensitivePatterns = [
      /auth/i,
      /token/i,
      /password/i,
      /secret/i,
      /key/i,
      /api[_-]?key/i,
      /private[_-]?key/i,
      /credential/i,
      /bearer/i,
      /jwt/i,
      /session/i,
      /cookie/i,
    ];

    const isSensitive = sensitivePatterns.some(pattern => pattern.test(key));
    
    if (isSensitive && value) {
      // Mask the value - show first 3 and last 3 characters
      const strValue = String(value);
      if (strValue.length > 6) {
        const start = strValue.substring(0, 3);
        const end = strValue.substring(strValue.length - 3);
        const middleLength = strValue.length - 6;
        return `${start}${'•'.repeat(middleLength)}${end}`;
      } else {
        return '•'.repeat(strValue.length);
      }
    }
    return value;
  };

  // Function to mask sensitive content in text
  const maskSensitiveContent = (content: string): string => {
    const sensitivePatterns = [
      /authKey:\s*["']([^"']+)["']/gi,
      /auth[_-]?key:\s*["']([^"']+)["']/gi,
      /token:\s*["']([^"']+)["']/gi,
      /password:\s*["']([^"']+)["']/gi,
      /secret:\s*["']([^"']+)["']/gi,
      /api[_-]?key:\s*["']([^"']+)["']/gi,
      /private[_-]?key:\s*["']([^"']+)["']/gi,
      /OT-[A-Za-z0-9]+/g,
      /Bearer\s+[A-Za-z0-9\-._~+/]+/gi,
    ];

    let maskedContent = content;
    
    sensitivePatterns.forEach(pattern => {
      maskedContent = maskedContent.replace(pattern, (match, capturedGroup) => {
        if (capturedGroup) {
          // For patterns with captured groups
          const value = capturedGroup;
          if (value.length > 6) {
            const start = value.substring(0, 3);
            const end = value.substring(value.length - 3);
            const middleLength = value.length - 6;
            return match.replace(value, `${start}${'•'.repeat(middleLength)}${end}`);
          } else {
            return match.replace(value, '•'.repeat(value.length));
          }
        } else {
          // For patterns without captured groups (like OT-xxx)
          const value = match;
          if (value.length > 6) {
            const start = value.substring(0, 3);
            const end = value.substring(value.length - 3);
            const middleLength = value.length - 6;
            return `${start}${'•'.repeat(middleLength)}${end}`;
          } else {
            return '•'.repeat(value.length);
          }
        }
      });
    });
    
    return maskedContent;
  };

  // Fetch system parameters data from API
  const fetchSystemParametersData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const isIpAddress = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$|^::1$|^localhost$/.test(project.projectName);
      
      let gitHistoryResponse;
      
      if (isIpAddress) {
        gitHistoryResponse = await gitLogAPI.getSystemParametersByIpAddress(project.projectName, project.isRobot);
      } else {
        gitHistoryResponse = await gitLogAPI.getSystemParametersHistory(project.projectName, {
          page: 1,
          limit: 50
        });
      }
      
      if (gitHistoryResponse.success) {
        let gitHistoryData = gitHistoryResponse.data.data || gitHistoryResponse.data;
        
        // Handle array response (API returns array with single item)
        if (Array.isArray(gitHistoryData) && gitHistoryData.length > 0) {
          gitHistoryData = gitHistoryData[0];
        }
        
        // Handle single document response
        if (gitHistoryData && !Array.isArray(gitHistoryData) && gitHistoryData.files) {
          const transformedData = {
            recentCommits: [{
              hash: gitHistoryData.commitHash || gitHistoryData._id,
              message: gitHistoryData.commitMessage || 'System Parameters Update',
              author: gitHistoryData.authorName || 'System',
              date: gitHistoryData.timestamp || gitHistoryData.updatedAt,
              branch: gitHistoryData.branchName || 'main',
              projectName: gitHistoryData.projectName,
              robotName: gitHistoryData.robotName,
              filesChanged: gitHistoryData.totalFiles || 0,
              totalParameters: gitHistoryData.totalParameters || 0,
              additions: gitHistoryData.files.reduce((sum: number, f: any) => sum + (f.additions || 0), 0),
              deletions: gitHistoryData.files.reduce((sum: number, f: any) => sum + (f.deletions || 0), 0),
              files: gitHistoryData.files
            }],
            currentBranch: gitHistoryData.branchName || 'main',
            branches: [gitHistoryData.branchName || 'main'],
            files: gitHistoryData.files,
            metadata: gitHistoryData.metadata || {}
          };
          
          gitHistoryData = transformedData;
        }
        
        setSystemParametersData(gitHistoryData);
      } else {
        setError(`Failed to fetch system parameters data. ${gitHistoryResponse.message || ''}`);
      }
    } catch (err: any) {
      console.error('Error fetching system parameters data:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch system parameters data');
    } finally {
      setLoading(false);
    }
  }, [project.projectName, project.isRobot]);

  useEffect(() => {
    fetchSystemParametersData();
  }, [fetchSystemParametersData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchSystemParametersData();
    setRefreshing(false);
  };

  const handleFileClick = (file: any) => {
    setSelectedFile(file);
    setShowFileDialog(true);
  };

  const toggleFileExpansion = (fileName: string) => {
    setExpandedFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fileName)) {
        newSet.delete(fileName);
      } else {
        newSet.add(fileName);
      }
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    return formatDate(dateString);
  };

  // --- (Loading and Error states remain the same) ---
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #1a2027 0%, #2d3748 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, md: 4 } // Responsive padding
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: '#8b5cf6', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'white' }}>
            Loading System Parameters...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #1a2027 0%, #2d3748 100%)',
          p: { xs: 2, md: 4 } // Responsive padding
        }}
      >
        <Container maxWidth="lg">
          <Button
            startIcon={<ArrowBack />}
            onClick={onBack}
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              mb: 2,
              '&:hover': {
                color: 'white',
                background: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Back to Dashboard
          </Button>
          <Alert severity="error">{error}</Alert>
        </Container>
      </Box>
    );
  }


  const commit = systemParametersData?.recentCommits?.[0];
  const files = systemParametersData?.files || [];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1a2027 0%, #2d3748 100%)',
        // --- RESPONSIVE REFACTOR ---
        p: { xs: 2, sm: 3, md: 4 }, // Responsive padding for the whole page
        // --- END REFACTOR ---
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={onBack}
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              mb: 2,
              '&:hover': {
                color: 'white',
                background: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Back to Dashboard
          </Button>

          {/* --- RESPONSIVE REFACTOR --- */}
          {/* This Box stacks the Title/Avatar and the Refresh button on mobile */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', md: 'center' }, 
            mb: 3,
            gap: 2 // Adds space when stacked
          }}>
            {/* Box to group Avatar and Title */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                sx={{
                  width: 60,
                  height: 60,
                  // mr: 3, // Removed margin from here
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                <Settings sx={{ fontSize: 30, color: 'white' }} />
              </Avatar>
              <Box sx={{ flex: 1, ml: { xs: 2, sm: 3 } }}> {/* Added margin here */}
                <Typography variant="h3" fontWeight="bold" sx={{ 
                  color: 'white', 
                  mb: 0.5,
                  fontSize: { xs: '2.25rem', md: '3rem' } // Responsive font size
                }}>
                  System Parameters
                </Typography>
                <Typography variant="h6" sx={{ 
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: { xs: '1rem', md: '1.25rem' } // Responsive font size
                }}>
                  {project.customName || (project.robotNames && project.robotNames.length > 0 ? project.robotNames[0] : project.projectName)} {project.customName && `- ${project.ipAddress}`}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                },
                width: { xs: '100%', md: 'auto' } // Full width on mobile
              }}
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>
          {/* --- END REFACTOR --- */}


          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)' }} />
        </Box>

        {/* Stats Cards - This Grid is already responsive */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* --- (Grid items remain the same, xs={12} sm={6} md={3} is responsive) --- */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'rgba(255, 255, 255, 0.95)', 
              backdropFilter: 'blur(20px)',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(139, 92, 246, 0.2)',
              }
            }}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Avatar sx={{ 
                  width: 56, 
                  height: 56, 
                  mx: 'auto', 
                  mb: 2,
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                }}>
                  <FileCopy sx={{ fontSize: 28 }} />
                </Avatar>
                <Typography variant="h3" fontWeight="bold" sx={{ color: '#8b5cf6', mb: 0.5 }}>
                  {files.length}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                  Parameter Files
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'rgba(255, 255, 255, 0.95)', 
              backdropFilter: 'blur(20px)',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(16, 185, 129, 0.2)',
              }
            }}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Avatar sx={{ 
                  width: 56, 
                  height: 56, 
                  mx: 'auto', 
                  mb: 2,
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                }}>
                  <Code sx={{ fontSize: 28 }} />
                </Avatar>
                <Typography variant="h3" fontWeight="bold" sx={{ color: '#10b981', mb: 0.5 }}>
                  {commit?.totalParameters || files.reduce((sum: number, f: any) => sum + (f.parameterCount || 0), 0)}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                  Total Parameters
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'rgba(255, 255, 255, 0.95)', 
              backdropFilter: 'blur(20px)',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(59, 130, 246, 0.2)',
              }
            }}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Avatar sx={{ 
                  width: 56, 
                  height: 56, 
                  mx: 'auto', 
                  mb: 2,
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                }}>
                  <Commit sx={{ fontSize: 28 }} />
                </Avatar>
                <Typography variant="h3" fontWeight="bold" sx={{ color: '#3b82f6', mb: 0.5 }}>
                  {commit?.additions || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                  Lines Added
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: 'rgba(255, 255, 255, 0.95)', 
              backdropFilter: 'blur(20px)',
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(239, 68, 68, 0.2)',
              }
            }}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Avatar sx={{ 
                  width: 56, 
                  height: 56, 
                  mx: 'auto', 
                  mb: 2,
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                }}>
                  <Delete sx={{ fontSize: 28 }} />
                </Avatar>
                <Typography variant="h3" fontWeight="bold" sx={{ color: '#ef4444', mb: 0.5 }}>
                  {commit?.deletions || 0}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
                  Lines Deleted
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Recent Change Info - This Card is already responsive with flexWrap */}
        {commit && (
          <Card sx={{ 
            background: 'rgba(255, 255, 255, 0.95)', 
            backdropFilter: 'blur(20px)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            mb: 4
          }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}> {/* Responsive padding */}
              <Box sx={{ 
                display: 'flex', 
                // --- RESPONSIVE REFACTOR ---
                flexDirection: { xs: 'column', sm: 'row' }, // Stack on mobile
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 2
                // --- END REFACTOR ---
              }}>
                <Avatar sx={{ 
                  width: 48, 
                  height: 48, 
                  // mr: 2, // Replaced by gap
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                }}>
                  <Commit sx={{ fontSize: 24 }} />
                </Avatar>
                <Box sx={{ flex: 1, width: '100%' }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#1a202c', mb: 0.5, wordBreak: 'break-word' }}>
                    {commit.message}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}> {/* flexWrap is great here */}
                    <Chip 
                      label={`Version: ${commit.branch}`} 
                      size="small" 
                      sx={{ 
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        fontWeight: 'bold'
                      }} 
                    />
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      <strong>Changed by:</strong> {commit.author}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280' }}>
                      <strong>Date:</strong> {formatRelativeTime(commit.date)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280', wordBreak: 'break-all' }}>
                      <strong>Change ID:</strong> {commit.hash?.substring(0, 7) || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Files List */}
        <Card sx={{ 
          background: 'rgba(255, 255, 255, 0.95)', 
          backdropFilter: 'blur(20px)',
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}> {/* Responsive padding */}
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#1a202c', mb: 3 }}>
              Parameter Files ({files.length})
            </Typography>

            {files.length === 0 ? (
              <Alert severity="info">
                No parameter files found for this robot.
              </Alert>
            ) : (
              <List sx={{ p: 0 }}> {/* Remove default list padding */}
                {files.map((file: any, index: number) => (
                  <React.Fragment key={file.fileName || index}>
                    <Accordion
                      expanded={expandedFiles.has(file.fileName)}
                      onChange={() => toggleFileExpansion(file.fileName)}
                      sx={{
                        mb: 2,
                        borderRadius: 2,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': {
                          boxShadow: '0 4px 16px rgba(139, 92, 246, 0.15)',
                        }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={{
                          '& .MuiAccordionSummary-content': {
                            alignItems: 'center',
                            width: '100%' // Ensure content takes full width
                          }
                        }}
                      >
                        {/* --- RESPONSIVE REFACTOR --- */}
                        {/* This Box stacks the Avatar and Text on mobile */}
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: { xs: 'flex-start', sm: 'center' }, 
                          flexDirection: { xs: 'column', sm: 'row' },
                          flex: 1,
                          width: '100%',
                          gap: 2 // Use gap for spacing
                        }}>
                          <Avatar sx={{ 
                            width: 40, 
                            height: 40, 
                            // mr: 2, // Replaced by gap
                            background: file.fileType === 'yaml' || file.fileType === 'yml' 
                              ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
                              : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          }}>
                            <Description sx={{ fontSize: 20 }} />
                          </Avatar>
                          <Box sx={{ flex: 1, width: '100%' }}> {/* Text block takes full width */}
                            <Typography variant="h6" fontWeight="bold" sx={{ color: '#1a202c', mb: 0.5, wordBreak: 'break-all' }}>
                              {file.fileName}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                              {/* ... (chips remain the same, flexWrap handles responsiveness) ... */}
                              <Chip 
                                label={file.fileType?.toUpperCase() || 'FILE'} 
                                size="small" 
                                sx={{ 
                                  background: 'rgba(139, 92, 246, 0.1)',
                                  color: '#8b5cf6',
                                  fontWeight: 'bold'
                                }} 
                              />
                              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                {file.parameterCount || 0} parameters
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                                {(file.fileSize / 1024).toFixed(2)} KB
                              </Typography>
                              {file.changeType && (
                                <Chip 
                                  label={file.changeType} 
                                  size="small" 
                                  sx={{ 
                                    background: file.changeType === 'modified' 
                                      ? 'rgba(245, 158, 11, 0.1)'
                                      : 'rgba(16, 185, 129, 0.1)',
                                    color: file.changeType === 'modified' 
                                      ? '#f59e0b'
                                      : '#10b981',
                                    fontWeight: 'bold'
                                  }} 
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                        {/* --- END REFACTOR --- */}
                      </AccordionSummary>
                      {/* --- RESPONSIVE REFACTOR --- */}
                      {/* Responsive padding to align with summary text */}
                      <AccordionDetails sx={{ 
                        pl: { xs: 2, sm: 'calc(40px + 16px)' } // Aligns with Avatar (40px) + gap (16px)
                      }}>
                        {/* Removed redundant Box with pl: 7 */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ color: '#6b7280', mb: 1 }}>
                            File Path
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#1a202c', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                            {file.filePath}
                          </Typography>
                        </Box>

                        {file.additions > 0 && file.deletions > 0 && (
                          <Box sx={{ mb: 2 }}>
                            {/* ... (changes chips, already responsive) ... */}
                          </Box>
                        )}

                        {/* This Box stacks the buttons on mobile */}
                        <Box sx={{ 
                          display: 'flex', 
                          gap: 1,
                          flexDirection: { xs: 'column', sm: 'row' } // Stack on xs
                        }}>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<Visibility />}
                            onClick={() => handleFileClick(file)}
                            sx={{
                              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                              },
                              width: { xs: '100%', sm: 'auto' } // Full width on mobile
                            }}
                          >
                            View Details
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ContentCopy />}
                            onClick={() => {
                              navigator.clipboard.writeText(file.filePath);
                            }}
                            sx={{
                              width: { xs: '100%', sm: 'auto' } // Full width on mobile
                            }}
                          >
                            Copy Path
                          </Button>
                        </Box>
                        {/* --- END REFACTOR --- */}
                      </AccordionDetails>
                    </Accordion>
                  </React.Fragment>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Container>

      {/* File Details Dialog - This is already responsive */}
      <Dialog
        open={showFileDialog}
        onClose={() => setShowFileDialog(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
            m: { xs: 2 } // Add margin on mobile
          }
        }}
      >
        {selectedFile && (
          <>
            <DialogTitle sx={{ 
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              color: 'white',
              fontWeight: 'bold',
              p: { xs: 2, sm: 3 } // Responsive padding
            }}>
              {selectedFile.fileName}
            </DialogTitle>
            <DialogContent sx={{ p: { xs: 2, sm: 3 } }}> {/* Responsive padding */}
              <Tabs 
                value={fileDialogTab} 
                onChange={(_, newValue) => setFileDialogTab(newValue)}
                sx={{ mb: 3 }}
                variant="scrollable" // Ensure tabs are scrollable on mobile
                scrollButtons="auto"
                allowScrollButtonsMobile
              >
                <Tab label="Content" />
                <Tab label="Parameters" />
                <Tab label="Diff" />
              </Tabs>

              {fileDialogTab === 0 && (
                <Paper sx={{ p: 2, background: '#1e293b', borderRadius: 2, maxHeight: '50vh', overflowY: 'auto' }}>
                  <pre style={{ 
                    margin: 0, 
                    color: '#e2e8f0', 
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {maskSensitiveContent(selectedFile.content)}
                  </pre>
                </Paper>
              )}

              {/* Parameter list is already responsive due to flex-direction: column */}
              {fileDialogTab === 1 && selectedFile.parameters && (
                <Box sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
                  <Box sx={{ 
                    mb: 3, 
                    p: 2, 
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    borderRadius: 2,
                    boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)'
                  }}>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 'bold', 
                        color: 'white',
                        textAlign: 'center'
                      }}
                    >
                      Parameters ({Object.keys(selectedFile.parameters).length})
                    </Typography>
                  </Box>
                  <List sx={{ p: 0 }}>
                    {Object.entries(selectedFile.parameters).map(([key, value]: [string, any], index: number) => (
                      <ListItem 
                        key={key} 
                        sx={{ 
                          border: '1px solid rgba(139, 92, 246, 0.2)',
                          borderRadius: 2,
                          mb: 2,
                          background: index % 2 === 0 ? 'rgba(139, 92, 246, 0.03)' : 'rgba(139, 92, 246, 0.05)',
                          transition: 'all 0.2s ease',
                          flexDirection: 'column', // This is key for responsiveness
                          alignItems: 'flex-start',
                          p: 2, // Add padding
                          '&:hover': {
                            background: 'rgba(139, 92, 246, 0.1)',
                            borderColor: 'rgba(139, 92, 246, 0.4)',
                            transform: 'translateX(4px)',
                          }
                        }}
                      >
                        {/* Key Section */}
                        <Box sx={{ width: '100%', mb: 1.5 }}>
                          <Chip 
                            label="Key" 
                            size="small" 
                            sx={{ 
                              mr: 1,
                              mb: 0.5,
                              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '0.7rem',
                              display: 'block',
                              width: 'fit-content'
                            }} 
                          />
                          <Typography 
                            variant="body1" 
                            fontWeight="bold" 
                            sx={{ 
                              color: '#1a202c',
                              fontFamily: 'monospace',
                              fontSize: '0.95rem',
                              wordBreak: 'break-word',
                              background: 'white',
                              padding: '8px 12px',
                              borderRadius: 1,
                              display: 'block',
                              width: '100%',
                              border: '2px solid #8b5cf6',
                              boxShadow: '0 2px 4px rgba(139, 92, 246, 0.1)'
                            }}
                          >
                            {key}
                          </Typography>
                        </Box>

                        {/* Value Section */}
                        <Box sx={{ width: '100%' }}>
                          <Chip 
                            label="Value" 
                            size="small" 
                            sx={{ 
                              mb: 0.5,
                              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '0.7rem',
                              display: 'block',
                              width: 'fit-content'
                            }} 
                          />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#1a202c',
                              fontFamily: 'monospace',
                              fontSize: '0.875rem',
                              background: 'white',
                              padding: '8px 12px',
                              borderRadius: 1,
                              display: 'block',
                              width: '100%',
                              wordBreak: 'break-word',
                              whiteSpace: 'pre-wrap',
                              border: '2px solid #10b981',
                              boxShadow: '0 2px 4px rgba(16, 185, 129, 0.1)'
                            }}
                          >
                            {typeof value === 'object' 
                              ? JSON.stringify(value, null, 2) 
                              : maskSensitiveValue(key, String(value))}
                          </Typography>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {fileDialogTab === 2 && selectedFile.diff && (
                <Paper sx={{ p: 2, background: '#1e293b', borderRadius: 2, maxHeight: '50vh', overflowY: 'auto' }}>
                  <pre style={{ 
                    margin: 0, 
                    color: '#e2e8f0', 
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                  }}>
                    {maskSensitiveContent(selectedFile.diff)}
                  </pre>
                </Paper>
              )}
            </DialogContent>
            <DialogActions sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1 }}>
              <Button 
                onClick={() => setShowFileDialog(false)}
                variant="outlined"
                sx={{ width: { xs: '100%', sm: 'auto' } }} // Full width on mobile
              >
                Close
              </Button>
              <Button 
                onClick={() => {
                  navigator.clipboard.writeText(selectedFile.content);
                }}
                variant="contained"
                startIcon={<ContentCopy />}
                sx={{
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                  },
                  width: { xs: '100%', sm: 'auto' } // Full width on mobile
                }}
              >
                Copy Content
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default SystemParametersGitHistory;